import { motion } from 'framer-motion';
import { History, Film, Tv } from 'lucide-react';
import type { WatchedMovie, WatchedEpisode } from '../../../services/traktApi';
import { LABELS } from '../../../data/labels';

export const RecentlyWatchedCard = ({ movies, episodes, loading, labels }: {
  movies: WatchedMovie[];
  episodes: WatchedEpisode[];
  loading: boolean;
  labels: typeof LABELS['fr'];
}) => {
  if (loading) {
    return (
      <div className="card card-hover p-6 h-full">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Date inconnue';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date inconnue';
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    } catch {
      return 'Date inconnue';
    }
  };

  // Fonction pour obtenir l'URL du poster
  const getMoviePoster = (item: WatchedMovie) => {
    if (item.movie.poster && item.movie.poster.startsWith('http')) {
      return item.movie.poster;
    }
    if (item.movie.ids.tmdb) {
      return `https://image.tmdb.org/t/p/w92/${item.movie.ids.tmdb}.jpg`;
    }
    return null;
  };

  const getShowPoster = (item: WatchedEpisode) => {
    if (item.show.poster && item.show.poster.startsWith('http')) {
      return item.show.poster;
    }
    if (item.show.ids.tmdb) {
      return `https://image.tmdb.org/t/p/w92/${item.show.ids.tmdb}.jpg`;
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="card card-hover p-6 h-full"
    >
      <div className="section-title mb-4">
        <History className="w-5 h-5 text-[#ed1c24]" />
        {labels.recently_watched}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Colonne Films */}
        <div>
          <h4 className="text-sm font-medium text-[#ed1c24] mb-3 flex items-center gap-2">
            <Film className="w-4 h-4" />
            {labels.movies}
          </h4>
          <div className="space-y-2">
            {movies.slice(0, 4).map((item, idx) => {
              const posterUrl = getMoviePoster(item);
              return (
                <motion.a
                  key={item.movie.ids.trakt}
                  href={`https://trakt.tv/movies/${item.movie.ids.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#f5f5f7] transition-colors group"
                >
                  <div className="w-10 h-14 rounded overflow-hidden bg-[#1a1a1a] flex-shrink-0">
                    {posterUrl ? (
                      <img
                        src={posterUrl}
                        alt={item.movie.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#666]">
                        <Film className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1d1d1f] group-hover:text-[#ed1c24] transition-colors truncate">{item.movie.title}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#8e8e93]">{formatDate(item.watched_at)}</span>
                      {item.rating && (
                        <span className="text-xs font-bold text-[#ed1c24]">★ {item.rating}</span>
                      )}
                    </div>
                  </div>
                </motion.a>
              );
            })}
            {movies.length === 0 && (
              <p className="text-xs text-[#8e8e93] italic">Aucun film récent</p>
            )}
          </div>
        </div>

        {/* Colonne Séries */}
        <div>
          <h4 className="text-sm font-medium text-[#ed1c24] mb-3 flex items-center gap-2">
            <Tv className="w-4 h-4" />
            {labels.shows}
          </h4>
          <div className="space-y-2">
            {episodes.slice(0, 4).map((item, idx) => {
              const posterUrl = getShowPoster(item);
              return (
                <motion.a
                  key={`${item.show.ids.trakt}-${item.episode.ids.trakt}`}
                  href={`https://trakt.tv/shows/${item.show.ids.slug}/seasons/${item.episode.season}/episodes/${item.episode.number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#f5f5f7] transition-colors group"
                >
                  <div className="w-10 h-14 rounded overflow-hidden bg-[#1a1a1a] flex-shrink-0">
                    {posterUrl ? (
                      <img
                        src={posterUrl}
                        alt={item.show.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#666]">
                        <Tv className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1d1d1f] group-hover:text-[#ed1c24] transition-colors truncate">{item.show.title}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#8e8e93]">
                        S{item.episode.season}E{item.episode.number} • {formatDate(item.watched_at)}
                      </span>
                      {item.rating && (
                        <span className="text-xs font-bold text-[#ed1c24]">★ {item.rating}</span>
                      )}
                    </div>
                  </div>
                </motion.a>
              );
            })}
            {episodes.length === 0 && (
              <p className="text-xs text-[#8e8e93] italic">Aucun épisode récent</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
